import { Payments } from "@/constants";
import { NET_DELAY } from "@/constants/config";

const GET_TYPE = /^(?:\[object )([A-z]+)(?:])$/;

function _type(val: unknown) {
  let match = Object.prototype.toString.call(val).match(GET_TYPE);
  if (match && match.length === 2) {
    return match[1];
  } else {
    console.error(`[参数]:${val},匹配结果:${match}`);
    return "";
  }
}

const tools = {
  __: () => {},
  type: (v: unknown) => _type(v),
  isElement: (v: unknown): v is Element => _type(v).indexOf("Element") !== -1,
  isArray: (v: unknown): v is [] => "Array" === _type(v),
  isNull: (v: unknown): v is null => "Null" === _type(v),
  isUndefined: (v: unknown): v is undefined => "Undefined" === _type(v),
  isBoolean: (v: unknown): v is boolean => "Boolean" === _type(v),
  isString: (v: unknown): v is string => "String" === _type(v),
  isNumber: (v: unknown): v is number =>
    !isNaN(Number(v)) && "Number" === _type(v),
  isObject: (v: unknown): v is object => "Object" === _type(v),
  isFunction: (v: unknown): v is Function => "Function" === _type(v),
  isEmpty: (v: unknown) => {
    if (tools.isUndefined(v) || tools.isNull(v)) return true;
    if (tools.isArray(v) && v.length === 0) return true;
    if (tools.isObject(v) && Object.keys(v).length === 0) return true;
    return tools.isString(v) && v === "";
  },
  dateFix: (time: number | Date | string) => {
    if (tools.isNumber(time)) return time;
    if (tools.type(time) === "Date") return time;
    if (!tools.isString(time)) {
      console.error("传入时间字符串");
      return new Date().toLocaleDateString();
    }
    time = String(time);
    // 2020-04-11T16:41:00
    if (time.indexOf("T") > -1) time = time.replace("T", " ");
    // 2020-04-11T16:41:00.452154
    if (time.indexOf(".") > -1) time = time.split(".")[0];
    let [ymd, hms] = time.split(" ");
    ymd = ymd.replace(/-/gm, "/");
    if (hms) {
      return `${ymd} ${hms}`;
    } else {
      return ymd;
    }
  },
  // 处理对象中的空值
  processParams: (
    obj: Record<string, unknown>,
    opt: [unknown] | unknown,
  ): Record<string, unknown> => {
    const res: Record<string, unknown> = {};
    const opts = (Array.isArray(opt) ? opt : [opt]).reduce(
      (acc, opt) => ({ ...acc, [opt]: 1 }),
      {},
    );
    Object.keys(obj).forEach((key) => {
      let value = obj[key];
      res[key] = (() => {
        if (opts["shake"]) {
          value = value === "" ? undefined : value;
        }
        if (opts["float"]) {
          if (tools.isNumber(value)) {
            return parseFloat(String(value)).toFixed(2);
          }
        }
        return value;
      })();
    });
    return res;
  },
};

// 补零
export function fixZero(val: number, max = 10) {
  // @ts-ignore
  if (!tools.isNumber(val)) throw new Error("Fn:fixZero val not a Number type");
  if (val < max) {
    const fillLen = String(max).length - String(val).length;
    return "0".repeat(fillLen) + val;
  } else {
    return val;
  }
}

// 解析时间
const parseTime = (date: Date) => {
  const years = date.getFullYear();
  const months = date.getMonth() + 1;
  const days = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  return {
    years,
    months,
    days,
    hours,
    minutes,
    seconds,
  };
};

// 时间格式化
export function dateFormat(
  val: number | string | Date,
  formatter = "",
): string {
  const strDate = tools.dateFix(val);
  const date = new Date(strDate);
  if (!formatter) return String(date);
  // 'Y:M:D h:f:m'
  const { years, months, days, hours, minutes, seconds } = parseTime(date);

  const o: Record<string, string | number> = {
    Y: years,
    M: fixZero(Number(months), 10),
    D: fixZero(Number(days), 10),
    h: fixZero(Number(hours), 10),
    m: fixZero(Number(minutes), 10),
    s: fixZero(Number(seconds), 10),
  };
  return formatter.replace(/[YMDhms]/g, function ($1): string {
    return String(o[$1]);
  });
}

// 金额格式化
type AmountFormatExtra = {
  places?: number;
  symbol?: string;
  thousand?: string;
  decimal?: string;
};

/**
 * 转换为标准金额值显示
 * @param number
 * @param param1
 * @returns
 */
export function amountFormat(
  number: number | string,
  { places, symbol, thousand, decimal }: AmountFormatExtra = {},
) {
  number = number || 0;
  places = !isNaN(places = Math.abs(places as number)) ? places : 2;
  symbol = symbol !== undefined ? symbol : "";
  thousand = thousand || ",";
  decimal = decimal || ".";
  let negative = Number(number) < 0 ? "-" : "";
  let i = parseInt(number = Math.abs(+number || 0).toFixed(places), 10) + "";
  let j: number;
  j = (j = i.length) > 3 ? j % 3 : 0;
  return (
    symbol +
    negative +
    (j ? i.substr(0, j) + thousand : "") +
    i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousand) +
    (places
      ? decimal +
        // @ts-ignore
        Math.abs(number - i)
          .toFixed(places)
          .slice(2)
      : "")
  );
}

// 设置年月日时分秒
// interface Moment extends Function {
//   (this: Moment, time: Date): Moment;
//   constructor(this: Moment, time: Date): Moment;
//   time: Date;
//   parse: () => void;
//   parseTime: {
//     years: number;
//     months: number;
//     days: number;
//     hours: number;
//     minutes: number;
//     seconds: number;
//   };
//   year: (num: number) => Moment;
//   month: (num: number) => Moment;
//   day: (num: number) => Moment;
//   hour: (num: number) => Moment;
//   minute: (num: number) => Moment;
//   second: (num: number) => Moment;
//   format: (...args: unknown[]) => string;
// }
export class Moment {
  time: Date;
  parseTime: {
    years: number;
    months: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  constructor(time: Date) {
    this.time = new Date(tools.dateFix(time || new Date()));
    const { years, months, days, hours, minutes, seconds } = parseTime(
      this.time,
    );
    this.parseTime = { years, months, days, hours, minutes, seconds };
  }

  year(num: number) {
    this.time.setFullYear(this.parseTime.years + num);
    return this;
  }

  month(num: number) {
    this.time.setMonth(this.parseTime.months + num);
    return this;
  }

  day(num: number) {
    this.time.setDate(this.parseTime.days + num);
    return this;
  }

  hour(num: number) {
    this.time.setHours(this.parseTime.days + num);
    return this;
  }

  minute(num: number) {
    this.time.setMinutes(this.parseTime.days + num);
    return this;
  }

  second(num: number) {
    this.time.setSeconds(this.parseTime.days + num);
    return this;
  }

  format(...args: string[]) {
    return dateFormat(this.time, ...args);
  }
}

export const formatDuration = (ms: number) => {
  if (ms < 0) ms = -ms;
  const time = {
    天: Math.floor(ms / 86400000),
    时: Math.floor(ms / 3600000) % 24,
    分: Math.floor(ms / 60000) % 60,
    秒: Math.floor(ms / 1000) % 60,
    // millisecond: Math.floor(ms) % 1000
  };
  return Object.entries(time)
    .filter((val) => val[1] !== 0)
    .map(([key, val]) => `${val} ${key}`)
    .join(", ");
};

/* `export const loopIf` 是一个接受函数 `fn` 和可选延迟时间 `ms` 作为参数的函数。它返回一个 promise，当 fn 返回一个 truthy 值时解析。如果 `fn`
返回一个假值，该函数将等待 `ms` 毫秒，然后再次调用 `fn`。此过程将一直持续到 fn 返回真值或抛出错误为止。 */
export const loopIf = (fn: Function, ms = 100): Promise<void> => {
  return new Promise((resolve, reject) => {
    const loopFn = () => {
      console.log("run loopFn");
      try {
        if (fn()) return resolve();
        else setTimeout(loopFn, ms);
      } catch (error) {
        reject(error);
      }
    };

    loopFn();
  });
};

/** 所有的api接口在提交金额时需要转换为2位小数点的浮点数 */
export const toMoney = (val: number | string) => {
  if (!val) return "";
  return parseFloat(String(val)).toFixed(2);
};

/** 支付方式采用的是 1 + 2 + 4 + 8 + 16 + 32 的方式传递的，所以需要解析 */
export const parsePayments = (payments: number): Payments[] => {
  const _payments = [
    Payments.WX_GROUP,
    Payments.DIGITAL_RMB,
    Payments.CRYPTO_COIN,
    Payments.WE_CHAT,
    Payments.ALI,
    Payments.BANK,
  ];
  const hits: number[] = [];
  _payments.forEach((v) => {
    if (payments >= v) {
      hits.push(v);
      payments = payments - v;
    }
  });
  return hits;
};

export const getServerTime = () => {
  return Date.now() + NET_DELAY;
};

export const checkPC = () => {
  if (
    navigator.userAgent.match(
      /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
    )
  ) {
    return false;
  } else {
    return true;
  }
};

export default tools;
